import { useState, useEffect, useRef, useCallback } from "react";
import { noteApi, BASE_ORIGIN } from "../api/api";
import { compressImage } from "../utils/imageCompressor";
import { formatDateTime, formatDate } from "../utils/dateUtils";
import MasterDetailView from "../components/MasterDetailView";
import "./common.css";

/* ── Toast ── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">
        {type === "success" ? <CheckIcon /> : <AlertIcon />}
      </span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ── Confirm Dialog ── */
function ConfirmDialog({ title, message, confirmLabel, confirmClass, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop" style={{ zIndex: 600 }}>
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><AlertIcon /></div>
        <h3 className="rv-dialog-title">{title}</h3>
        <p className="rv-dialog-desc">{message}</p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Go Back</button>
          <button className={`rv-btn ${confirmClass}`} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Image URL normalization helper ── */
export function toAbsoluteImageUrl(src) {
  if (!src || typeof src !== "string") return null;
  const trimmed = src.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("data:")) {
    return trimmed;
  }
  return `${BASE_ORIGIN}/${trimmed.replace(/^\//, "")}`;
}

export function fixImageUrls(html) {
  if (!html || typeof html !== "string") return html || "";
  return html.replace(/<img([^>]+)src=["'](?!http|data:)([^"']+)["']/gi, (match, pre, src) => {
    const abs = toAbsoluteImageUrl(src);
    return `<img${pre}src="${abs}"`;
  });
}

export function extractFirstImage(noteOrHtml, delta) {
  if (!noteOrHtml) return null;

  // If a full note object is passed
  if (typeof noteOrHtml === "object") {
    const n = noteOrHtml;
    if (n.imageUrl) return toAbsoluteImageUrl(n.imageUrl);
    if (n.image) return toAbsoluteImageUrl(n.image);
    if (n.filePath) return toAbsoluteImageUrl(n.filePath);
    return extractFirstImage(
      n.contentHtml || n.html || n.content || n.preview,
      n.contentDelta || n.delta
    );
  }

  const html = typeof noteOrHtml === "string" ? noteOrHtml : "";
  if (html) {
    const match = html.match(/<img[^>]+src=["']([^"']+)["']/i);
    if (match && match[1]) {
      return toAbsoluteImageUrl(match[1]);
    }
  }

  const deltaInput = delta || (typeof noteOrHtml === "string" && noteOrHtml.startsWith("{") ? noteOrHtml : null);
  if (deltaInput) {
    try {
      const parsed = typeof deltaInput === "string" ? JSON.parse(deltaInput) : deltaInput;
      const ops = parsed?.ops || (Array.isArray(parsed) ? parsed : null);
      if (ops) {
        for (const op of ops) {
          if (op?.insert?.image) {
            return toAbsoluteImageUrl(op.insert.image);
          }
        }
      }
    } catch {
      // Ignore JSON parse errors
    }
  }
  return null;
}

/* ── Quill Editor ── */
function QuillEditor({ initialDelta, initialHtml, initialText, onChange, disabled }) {
  const containerRef = useRef(null);
  const quillRef     = useRef(null);
  const onChangeRef  = useRef(onChange);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Helper to load content into Quill with absolute image URLs
  const loadContent = useCallback((quill, delta, html, text) => {
    if (!quill) return;
    if (delta) {
      try {
        let parsed = typeof delta === "string" ? JSON.parse(delta) : delta;
        if (parsed?.ops && Array.isArray(parsed.ops)) {
          parsed.ops = parsed.ops.map(op => {
            if (op?.insert?.image && typeof op.insert.image === "string") {
              return { ...op, insert: { ...op.insert, image: toAbsoluteImageUrl(op.insert.image) } };
            }
            return op;
          });
          quill.setContents(parsed, "silent");
          return;
        }
      } catch {
        // Fallback to HTML if delta parsing fails
      }
    }
    if (html && html.trim() && html !== "<p><br></p>") {
      quill.clipboard.dangerouslyPasteHTML(fixImageUrls(html));
      return;
    }
    if (text && text.trim()) {
      quill.setText(text.trim() + "\n");
    }
  }, []);

  useEffect(() => {
    if (!containerRef.current || quillRef.current) return;
    if (!window.Quill) {
      console.error("Quill not loaded — add CDN links to public/index.html.");
      return;
    }

    const quill = new window.Quill(containerRef.current, {
      theme:    "snow",
      readOnly: disabled ?? false,
      placeholder: "Write customer requirements here — design preference, budget, occasion, size, metal type…",
      modules: {
        toolbar: disabled ? false : {
          container: [
            [{ size: ["small", false, "large", "huge"] }],
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            ["blockquote"],
            ["image", "link"],
            ["clean"],
          ],
          handlers: {
            image: () => {
              const input   = document.createElement("input");
              input.type    = "file";
              input.accept  = "image/jpeg,image/png,image/gif,image/webp,image/*";
              input.onchange = async () => {
                const file = input.files?.[0];
                if (!file) return;

                try {
                  // 1. Compress image to clean lightweight resolution (< 150KB)
                  const compressed = await compressImage(file, 1200, 0.82);

                  let finalUrl = null;

                  // 2. Try server upload
                  try {
                    const res = await noteApi.uploadImage(compressed);
                    const rawPath =
                      res?.url ||
                      res?.data?.url ||
                      res?.filePath ||
                      res?.filepath ||
                      res?.data?.filePath ||
                      res?.data?.filepath ||
                      res?.path ||
                      res?.imageUrl ||
                      (typeof res === "string" ? res : null);
                    if (rawPath) {
                      finalUrl = toAbsoluteImageUrl(rawPath);
                    }
                  } catch (err) {
                    console.warn("Server upload endpoint unavailable, embedding compressed image directly:", err);
                  }

                  // 3. If server didn't provide a path, embed as base64 data URL
                  if (!finalUrl) {
                    finalUrl = await new Promise((resolve, reject) => {
                      const reader = new FileReader();
                      reader.onload = () => resolve(reader.result);
                      reader.onerror = reject;
                      reader.readAsDataURL(compressed);
                    });
                  }

                  if (!finalUrl) throw new Error("Could not process image file.");

                  const q = quillRef.current;
                  if (!q) return;

                  const range = q.getSelection(true);
                  const idx = range && typeof range.index === "number" ? range.index : (q.getLength() || 0);
                  q.insertEmbed(idx, "image", finalUrl, "user");
                  q.setSelection(idx + 1, "silent");

                  // Notify form of change immediately
                  if (onChangeRef.current) {
                    onChangeRef.current({
                      delta:     JSON.stringify(q.getContents()),
                      html:      q.root.innerHTML,
                      plainText: q.getText().trim(),
                    });
                  }
                } catch (err) {
                  console.error("Image attachment failed:", err);
                  alert("Could not attach photo: " + (err.message || "Please try a different image."));
                }
              };
              input.click();
            },
          },
        },
      },
    });

    /* ── Set initial content ── */
    loadContent(quill, initialDelta, initialHtml, initialText);

    quill.on("text-change", () => {
      onChangeRef.current({
        delta:     JSON.stringify(quill.getContents()),
        html:      quill.root.innerHTML,
        plainText: quill.getText().trim(),
      });
    });

    quillRef.current = quill;

    return () => {
      /* ── Cleanup ── */
      const toolbar = quill.getModule("toolbar");
      if (toolbar?.container) toolbar.container.remove();
      if (containerRef.current) containerRef.current.innerHTML = "";
      quillRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        minHeight:  220,
        fontSize:   13.5,
        fontFamily: "var(--b-font, sans-serif)",
        opacity:    disabled ? 0.7 : 1,
      }}
    />
  );
}

/* ── Note Editor Modal ── */
function NoteModal({ initial, onSave, onClose, saving }) {
  const isEdit = !!initial?.noteID;

  const [loadingDetail, setLoadingDetail] = useState(isEdit);
  const [title,         setTitle]         = useState(initial?.title ?? "");
  const [titleErr,      setTitleErr]      = useState("");
  const [editorData,    setEditorData]    = useState({
    delta:     initial?.contentDelta ?? "",
    html:      initial?.contentHtml  ?? "",
    plainText: initial?.plainText    ?? initial?.preview ?? "",
  });
  const [contentErr,    setContentErr]    = useState("");

  // Fetch full details if editing a note to ensure full HTML & images are loaded
  useEffect(() => {
    if (initial?.noteID) {
      setLoadingDetail(true);
      noteApi.getById(initial.noteID)
        .then(res => {
          const detail = res?.data || res;
          if (detail) {
            if (detail.title) setTitle(detail.title);
            setEditorData({
              delta:     detail.contentDelta || initial?.contentDelta || "",
              html:      detail.contentHtml || detail.content || initial?.contentHtml || "",
              plainText: detail.plainText || detail.preview || initial?.plainText || initial?.preview || "",
            });
          }
        })
        .catch(err => {
          console.warn("Could not load full note detail:", err);
        })
        .finally(() => setLoadingDetail(false));
    } else {
      setLoadingDetail(false);
    }
  }, [initial]);

  const handleEditorChange = useCallback((data) => {
    setEditorData(data);
    if (data.plainText.trim() || (data.html && data.html.includes("<img"))) {
      setContentErr("");
    }
  }, []);

  const handleSubmit = () => {
    let valid = true;
    if (!title.trim()) {
      setTitleErr("Please enter the customer name or title.");
      valid = false;
    }
    const hasContent = editorData.plainText.trim() || (editorData.html && editorData.html.includes("<img"));
    if (!hasContent) {
      setContentErr("Please enter the customer's requirements or details.");
      valid = false;
    } else if (
      (editorData.delta && editorData.delta.toLowerCase().includes("data:image")) ||
      (editorData.html && editorData.html.toLowerCase().includes("data:image"))
    ) {
      setContentErr("Directly pasting images is not supported. Please click the photo icon on the toolbar to attach customer photos.");
      valid = false;
    } else {
      const totalBytes = (editorData.delta ? editorData.delta.length : 0) + (editorData.html ? editorData.html.length : 0);
      if (totalBytes > 2 * 1024 * 1024) {
        setContentErr("Note content is too large (exceeds 2 MB limit). Please use smaller photos.");
        valid = false;
      }
    }
    if (!valid) return;

    onSave({
      ...(isEdit ? { noteID: initial.noteID, versionNo: initial.versionNo } : {}),
      title:        title.trim(),
      contentDelta: editorData.delta,
      contentHtml:  editorData.html,
      plainText:    editorData.plainText,
    });
  };

  return (
    <div className="rv-dialog-backdrop" style={{ zIndex: 500 }}>
      <div
        className="rv-dialog rv-dialog--form"
        style={{ maxWidth: 820, width: "100%", maxHeight: "92vh", display: "flex", flexDirection: "column" }}
      >
        {/* Header */}
        <div className="rv-modal-header" style={{ flexShrink: 0 }}>
          <div className="rv-modal-icon"><NoteIcon /></div>
          <div>
            <h3 className="rv-modal-title">{isEdit ? "Edit Note" : "New Customer Note"}</h3>
            <p className="rv-modal-sub">
              {isEdit
                ? "Update this customer requirement note."
                : "Record what the customer is looking for."}
            </p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        {/* Body */}
        <div className="rv-modal-body" style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>

          {/* Title */}
          <div className="rv-field">
            <label className="rv-label">
              Customer Name &amp; Title <span className="rv-required">*</span>
            </label>
            <input
              className={`rv-input${titleErr ? " rv-input--err" : ""}`}
              placeholder="e.g. Vaidik Patel — Rose Gold Ring"
              value={title}
              onChange={e => { setTitle(e.target.value); setTitleErr(""); }}
            />
            {titleErr && <p className="rv-field-err">{titleErr}</p>}
            <p className="rv-field-hint">
              Write the customer name and what they're looking for in the title.
            </p>
          </div>

          {/* Quill Editor */}
          <div className="rv-field">
            <label className="rv-label">
              Customer Requirements &amp; Details <span className="rv-required">*</span>
            </label>
            <div style={{
              border:       `1px solid ${contentErr ? "#c0392b" : "rgba(185,167,146,.35)"}`,
              borderRadius: 10,
              overflow:     "hidden",
              background:   "#fff",
              minHeight:    220,
            }}>
              {loadingDetail ? (
                <div style={{ padding: "48px 0", textAlign: "center", color: "var(--taupe)" }}>
                  <div className="rv-loader" />
                  <p style={{ marginTop: 10, fontSize: 12 }}>Loading note details…</p>
                </div>
              ) : (
                <QuillEditor
                  initialDelta={editorData.delta}
                  initialHtml={editorData.html}
                  initialText={editorData.plainText}
                  onChange={handleEditorChange}
                />
              )}
            </div>
            {contentErr && <p className="rv-field-err" style={{ marginTop: 4 }}>{contentErr}</p>}
            <p className="rv-field-hint">
              Use the toolbar to format text. Click the photo icon to attach a reference photo.
            </p>
          </div>

        </div>

        {/* Footer */}
        <div className="rv-modal-footer" style={{ flexShrink: 0 }}>
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving && <span className="rv-spinner" />}
            {isEdit ? "Update Note" : "Save Note"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Note View (Read-Only) Modal ── */
function NoteViewModal({ note, onEdit, onDelete, onHistory, onClose }) {
  const [fullNote, setFullNote] = useState(note);
  const [loading,  setLoading]  = useState(!note?.contentHtml && !note?.contentDelta);

  useEffect(() => {
    if (note?.noteID) {
      setLoading(true);
      noteApi.getById(note.noteID)
        .then(res => {
          const detail = res?.data || res;
          if (detail) setFullNote(prev => ({ ...prev, ...detail }));
        })
        .catch(err => {
          console.warn("Could not load full note:", err);
        })
        .finally(() => setLoading(false));
    }
  }, [note?.noteID]);

  const fmt = d => formatDateTime(d);

  const renderedHtml = fullNote?.contentHtml
    ? fixImageUrls(fullNote.contentHtml)
    : fullNote?.plainText
      ? `<p>${fullNote.plainText.replace(/\n/g, "<br/>")}</p>`
      : fullNote?.preview
        ? `<p>${fullNote.preview.replace(/\n/g, "<br/>")}</p>`
        : "<p style='color:var(--taupe);font-style:italic;'>No details recorded yet.</p>";

  return (
    <div className="rv-dialog-backdrop" onClick={onClose} style={{ zIndex: 500 }}>
      <div
        className="rv-dialog rv-dialog--form"
        style={{ maxWidth: 760, width: "100%", maxHeight: "92vh", display: "flex", flexDirection: "column" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="rv-modal-header" style={{ flexShrink: 0 }}>
          <div className="rv-modal-icon"><NoteIcon /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <h3 className="rv-modal-title" style={{ wordBreak: "break-word", margin: 0 }}>
                {fullNote?.title || "Customer Note"}
              </h3>
              {(fullNote?.versionNo ?? 1) > 1 && (
                <span style={{
                  fontSize: 10.5, fontWeight: 700, color: "var(--taupe)",
                  background: "rgba(185,167,146,.15)", padding: "2px 8px",
                  borderRadius: 20, flexShrink: 0,
                }}>
                  Edited {(fullNote?.versionNo ?? 1) - 1} time{(fullNote?.versionNo ?? 1) - 1 === 1 ? "" : "s"}
                </span>
              )}
            </div>
            <p className="rv-modal-sub" style={{ marginTop: 2 }}>
              Last updated on {fmt(fullNote?.lastUPDTTM)}
            </p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        {/* Body */}
        <div className="rv-modal-body" style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
          <div className="rv-field">
            <label className="rv-label" style={{ marginBottom: 6 }}>Customer Requirements &amp; Photos</label>
            {loading ? (
              <div style={{ padding: "48px 0", textAlign: "center", color: "var(--taupe)", background: "#FAF7F4", borderRadius: 10 }}>
                <div className="rv-loader" />
                <p style={{ marginTop: 10, fontSize: 12.5 }}>Loading note details…</p>
              </div>
            ) : (
              <div style={{
                background: "#FAF7F4",
                border: "1px solid rgba(185,167,146,.25)",
                borderRadius: 10,
                padding: "18px 22px",
                color: "var(--dark)",
                fontSize: 13.5,
                lineHeight: 1.7,
              }}>
                <div
                  className="ql-editor"
                  style={{ padding: 0, minHeight: "auto" }}
                  dangerouslySetInnerHTML={{ __html: renderedHtml }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="rv-modal-footer" style={{ flexShrink: 0, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="rv-btn rv-btn--ghost"
              style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--taupe)" }}
              onClick={() => {
                onClose();
                onHistory(fullNote);
              }}
            >
              <HistoryIcon /> Past Versions
            </button>
            <button
              className="rv-btn rv-btn--ghost"
              style={{ display: "flex", alignItems: "center", gap: 6, color: "#c0392b" }}
              onClick={() => {
                onClose();
                onDelete(fullNote);
              }}
            >
              <TrashIcon /> Delete Note
            </button>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button className="rv-btn rv-btn--ghost" onClick={onClose}>Close</button>
            <button
              className="rv-btn rv-btn--primary"
              style={{ display: "flex", alignItems: "center", gap: 6 }}
              onClick={() => {
                onClose();
                onEdit(fullNote);
              }}
            >
              <EditIcon /> Edit Note
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Note Version History Full View ── */
function NoteHistoryView({ note, onBack, onRestored }) {
  const [history,    setHistory]    = useState([]);
  const [selected,   setSelected]   = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [restoring,  setRestoring]  = useState(null);
  const [confirm,    setConfirm]    = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const res = await noteApi.getHistory(note.noteID);
      const list = Array.isArray(res) ? res : res?.data ?? [];
      setHistory(list);
      if (list.length > 0) {
        setSelected(list[0]); // Default to latest version
      }
    } catch {
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [note.noteID]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleRestore = async (versionNo) => {
    setRestoring(versionNo);
    try {
      await noteApi.restore(note.noteID, versionNo);
      onRestored();
      await fetchHistory();
    } catch (err) {
      alert("Could not restore version: " + (err.message || "Unknown error"));
    } finally {
      setRestoring(null);
      setConfirm(null);
    }
  };

  const fmt = d => formatDateTime(d);

  const ACTION_COLOR = {
    Created:  { bg: "rgba(39,174,96,.1)",    text: "#27ae60", border: "rgba(39,174,96,.3)" },
    Updated:  { bg: "rgba(91,127,168,.1)",   text: "#4a6fa5", border: "rgba(91,127,168,.3)" },
    Deleted:  { bg: "rgba(192,57,43,.1)",    text: "#c0392b", border: "rgba(192,57,43,.3)" },
    Restored: { bg: "rgba(185,167,146,.15)", text: "#9e8872", border: "rgba(185,167,146,.35)" },
  };

  const selectedHtml = selected?.contentHtml
    ? fixImageUrls(selected.contentHtml)
    : selected?.plainText
      ? `<p>${selected.plainText.replace(/\n/g, "<br/>")}</p>`
      : selected?.preview
        ? `<p>${selected.preview.replace(/\n/g, "<br/>")}</p>`
        : "<p style='color:var(--taupe);font-style:italic;'>No details recorded in this version.</p>";

  const isLatest = history.length > 0 && selected?.noteHistoryID === history[0]?.noteHistoryID;

  return (
    <>
      {confirm && (
        <ConfirmDialog
          title="Restore this version?"
          message={`This will replace the current active note with the contents of Version ${confirm}. Don't worry, your current note will still be saved in the version history.`}
          confirmLabel="Yes, Restore Version"
          confirmClass="rv-btn--primary"
          onConfirm={() => handleRestore(confirm)}
          onCancel={() => setConfirm(null)}
        />
      )}

      <MasterDetailView
        title={`Change History — ${note.title}`}
        subtitle="View past edits, see what changed, and restore an earlier version if needed."
        backLabel="Back to Notes"
        onBack={onBack}
        badgeText={`${history.length} saved version${history.length === 1 ? "" : "s"}`}
        timelineTitle="Saved Versions"
        items={history}
        selectedItem={selected}
        onSelectItem={item => setSelected(item)}
        loading={loading}
        emptyTitle="No Past Versions Found"
        emptyMessage="This note has not been edited yet."
        renderTimelineItem={(h, idx, isItemActive) => {
          const ac = ACTION_COLOR[h.actionType] ?? ACTION_COLOR.Updated;
          const isCur = idx === 0;
          const hasImg = (h.contentHtml && h.contentHtml.includes("<img")) || extractFirstImage(h);

          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{
                    fontSize: 11, fontWeight: 800,
                    background: isCur ? "var(--dark)" : "rgba(185,167,146,.2)",
                    color: isCur ? "#fff" : "var(--dark)",
                    padding: "2px 8px", borderRadius: 14,
                  }}>
                    Version {h.versionNo}
                  </span>
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    padding: "2px 7px", borderRadius: 12,
                    background: ac.bg, color: ac.text, border: `1px solid ${ac.border}`,
                  }}>
                    {h.actionType}
                  </span>
                </div>

                {isCur && (
                  <span style={{
                    fontSize: 9.5, fontWeight: 700, color: "#27ae60",
                    background: "rgba(39,174,96,.12)", padding: "2px 7px", borderRadius: 10,
                  }}>
                    Current
                  </span>
                )}
              </div>

              <div style={{ fontSize: 12.5, fontWeight: 600, color: isItemActive ? "var(--taupe-dark)" : "var(--dark)" }}>
                {h.title}
              </div>

              {h.plainText && (
                <div style={{
                  fontSize: 11.5, color: "var(--taupe-dark)",
                  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                  overflow: "hidden", lineHeight: 1.45,
                }}>
                  {h.plainText}
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 2 }}>
                <span style={{ fontSize: 10.5, color: "var(--taupe)" }}>
                  {fmt(h.lastUPDTTM)}
                </span>
                {hasImg && (
                  <span style={{ fontSize: 10, color: "var(--taupe)", display: "flex", alignItems: "center", gap: 4 }}>
                    📷 Photo attached
                  </span>
                )}
              </div>
            </div>
          );
        }}
        renderDetail={(sel) => (
          <>
            {/* Detail Header */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              borderBottom: "1px solid rgba(185,167,146,.25)",
              paddingBottom: 16,
              flexWrap: "wrap",
              gap: 12,
            }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
                  <h3 style={{ fontSize: 17, fontWeight: 700, color: "var(--dark)", margin: 0 }}>
                    {sel.title}
                  </h3>
                  <span style={{
                    fontSize: 11, fontWeight: 700,
                    padding: "3px 10px", borderRadius: 20,
                    background: "var(--dark)", color: "#fff",
                  }}>
                    Version {sel.versionNo}
                  </span>
                  {isLatest ? (
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: "#27ae60",
                      background: "rgba(39,174,96,.12)", padding: "3px 10px", borderRadius: 20,
                    }}>
                      Current Active Note
                    </span>
                  ) : (
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: "var(--taupe)",
                      background: "rgba(185,167,146,.15)", padding: "3px 10px", borderRadius: 20,
                    }}>
                      Previous Saved Version
                    </span>
                  )}
                </div>

                <p style={{ margin: 0, fontSize: 12, color: "var(--taupe)" }}>
                  Saved on {fmt(sel.lastUPDTTM)} • <strong>{sel.actionType === "Created" ? "Initial note" : sel.actionType === "Restored" ? "Restored note" : "Edited note"}</strong>
                </p>
              </div>

              {/* Restore Action */}
              <div>
                {isLatest ? (
                  <span style={{
                    fontSize: 12, color: "var(--taupe)",
                    background: "#FAF4EF", padding: "6px 14px", borderRadius: 8,
                    border: "1px solid rgba(185,167,146,.3)", display: "inline-block",
                  }}>
                    ✓ Current Active Version
                  </span>
                ) : (
                  <button
                    className="rv-btn rv-btn--primary"
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                    onClick={() => setConfirm(sel.versionNo)}
                    disabled={restoring === sel.versionNo}
                  >
                    {restoring === sel.versionNo ? (
                      <span className="rv-spinner" />
                    ) : (
                      <HistoryIcon />
                    )}
                    Restore this Version (v{sel.versionNo})
                  </button>
                )}
              </div>
            </div>

            {/* Rich Content View */}
            <div>
              <label className="rv-label" style={{ marginBottom: 8, fontSize: 12 }}>
                Customer Requirements &amp; Photos
              </label>
              <div style={{
                background: "#FAF7F4",
                border: "1px solid rgba(185,167,146,.25)",
                borderRadius: 10,
                padding: "20px 24px",
                color: "var(--dark)",
                fontSize: 14,
                lineHeight: 1.7,
              }}>
                <div
                  className="ql-editor"
                  style={{ padding: 0, minHeight: "auto" }}
                  dangerouslySetInnerHTML={{ __html: selectedHtml }}
                />
              </div>
            </div>
          </>
        )}
      />
    </>
  );
}

/* ── Note Card ── */
function NoteCard({ note, onView, onEdit, onDelete, onHistory }) {
  const fmt = d => formatDate(d);

  const firstImg = extractFirstImage(note);

  return (
    <div
      style={{
        background:    "#fff",
        border:        "1px solid rgba(185,167,146,.2)",
        borderRadius:  12,
        padding:       "16px 18px",
        display:       "flex",
        flexDirection: "column",
        gap:           10,
        cursor:        "pointer",
        transition:    "transform .18s, box-shadow .18s, border-color .18s",
      }}
      onClick={() => onView(note)}
      onMouseEnter={e => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 6px 20px rgba(185,167,146,.22)";
        e.currentTarget.style.borderColor = "var(--taupe, #B9A792)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = "none";
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.borderColor = "rgba(185,167,146,.2)";
      }}
    >
      {/* Title row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: "var(--dark)", margin: 0, lineHeight: 1.4 }}>
          {note.title}
        </h4>
        {(note.versionNo ?? 1) > 1 && (
          <span style={{
            fontSize: 10, fontWeight: 600, color: "var(--taupe)",
            background: "rgba(185,167,146,.12)", padding: "2px 8px",
            borderRadius: 20, whiteSpace: "nowrap", flexShrink: 0,
          }}>
            Edited {(note.versionNo ?? 1) - 1} time{(note.versionNo ?? 1) - 1 === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {/* Content & optional image thumbnail */}
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        {firstImg && (
          <div style={{
            width: 68,
            height: 68,
            borderRadius: 8,
            overflow: "hidden",
            background: "#FAF4EF",
            border: "1px solid rgba(185,167,146,.3)",
            flexShrink: 0,
            boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
          }}>
            <img
              src={firstImg}
              alt="attachment"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              onError={e => { e.currentTarget.parentElement.style.display = "none"; }}
            />
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          {note.plainText || note.preview ? (
            <p style={{
              fontSize: 12.5, color: "var(--taupe-dark)", margin: 0, lineHeight: 1.6,
              display: "-webkit-box", WebkitLineClamp: firstImg ? 2 : 3,
              WebkitBoxOrient: "vertical", overflow: "hidden",
            }}>
              {note.plainText || note.preview}
            </p>
          ) : (
            <p style={{ fontSize: 12, color: "var(--taupe)", fontStyle: "italic", margin: 0 }}>
              {firstImg ? "Reference photo attached" : "No details written yet."}
            </p>
          )}
        </div>
      </div>

      {/* Footer row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto", paddingTop: 4 }}>
        <span style={{ fontSize: 11, color: "var(--taupe)" }}>
          Updated on {fmt(note.lastUPDTTM)}
        </span>
        <div style={{ display: "flex", gap: 6 }} onClick={e => e.stopPropagation()}>
          <button
            className="rv-action-btn"
            title="Past Versions"
            style={{ color: "var(--taupe)" }}
            onClick={(e) => {
              e.stopPropagation();
              onHistory(note);
            }}
          >
            <HistoryIcon />
          </button>
          <button
            className="rv-action-btn rv-action-btn--edit"
            title="Edit Note"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(note);
            }}
          >
            <EditIcon />
          </button>
          <button
            className="rv-action-btn rv-action-btn--del"
            title="Delete Note"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(note);
            }}
          >
            <TrashIcon />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN NOTES PAGE
══════════════════════════════════════ */
export default function NotesPage() {
  const [notes,       setNotes]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search,      setSearch]      = useState("");

  const [viewModal, setViewModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [confirm,   setConfirm]   = useState(null);
  const [history,   setHistory]   = useState(null);
  const [toast,     setToast]     = useState(null);

  const searchTimer = useRef(null);

  const fetchNotes = useCallback(async (q = "") => {
    try {
      setLoading(true);
      const data = await noteApi.getAll(q);
      setNotes(Array.isArray(data) ? data : data?.data ?? []);
    } catch (err) {
      showToast(err.message || "Failed to load notes.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  /* ── Auto open New Note modal from Executive Short-Paths ── */
  useEffect(() => {
    const autoModal = sessionStorage.getItem("reva_auto_modal");
    if (autoModal === "new-note" || autoModal === "add-note") {
      sessionStorage.removeItem("reva_auto_modal");
      setEditModal({ data: {} });
    }
  }, []);

  useEffect(() => {
    const handleOpenModal = (e) => {
      if (e?.detail === "new-note" || e?.detail === "add-note") {
        sessionStorage.removeItem("reva_auto_modal");
        setEditModal({ data: {} });
      }
    };
    window.addEventListener("reva:open_modal", handleOpenModal);
    return () => window.removeEventListener("reva:open_modal", handleOpenModal);
  }, []);

  const handleSearchInput = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val);
      fetchNotes(val);
    }, 400);
  };

  const showToast = (message, type = "success") => setToast({ message, type });

  /* ── Save ── */
  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (formData.noteID) {
        await noteApi.update(formData);
        showToast("Note updated successfully.");
      } else {
        await noteApi.create(formData);
        showToast("Note saved successfully.");
      }
      setEditModal(null);
      fetchNotes(search);
    } catch (err) {
      showToast(err.message || "Something went wrong.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await noteApi.remove(confirm.noteID);
      showToast("Note deleted.");
      setConfirm(null);
      if (viewModal?.noteID === confirm.noteID) setViewModal(null);
      fetchNotes(search);
    } catch (err) {
      showToast(err.message || "Failed to delete note.", "error");
    }
  };

  if (history) {
    return (
      <NoteHistoryView
        note={history}
        onBack={() => setHistory(null)}
        onRestored={() => fetchNotes(search)}
      />
    );
  }

  return (
    <div className="rv-inv-page">

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* View Modal (Opened by clicking anywhere on the card) */}
      {viewModal && (
        <NoteViewModal
          note={viewModal}
          onEdit={n => {
            setViewModal(null);
            setEditModal({ data: n });
          }}
          onDelete={n => setConfirm(n)}
          onHistory={n => setHistory(n)}
          onClose={() => setViewModal(null)}
        />
      )}

      {/* Edit / Create Modal */}
      {editModal && (
        <NoteModal
          initial={editModal.data}
          onSave={handleSave}
          onClose={() => setEditModal(null)}
          saving={saving}
        />
      )}

      {confirm && (
        <ConfirmDialog
          title="Delete this note?"
          message={`"${confirm.title}" will be permanently removed. This action cannot be reversed.`}
          confirmLabel="Yes, Delete Note"
          confirmClass="rv-btn--danger"
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}

      {/* Topbar */}
      <div className="rv-inv-topbar">
        <div className="rv-inv-topbar-left">
          <h2 className="rv-inv-heading">Customer Notes</h2>
          <span className="rv-inv-count">{notes.length} {notes.length === 1 ? "note" : "notes"}</span>
        </div>
        <button className="rv-btn rv-btn--primary" onClick={() => setEditModal({ data: {} })}>
          <PlusIcon /> Add Note
        </button>
      </div>

      {/* Search */}
      <div className="rv-inv-filters">
        <div className="rv-inv-search">
          <SearchIcon />
          <input
            placeholder="Search by customer name or requirements…"
            value={searchInput}
            onChange={e => handleSearchInput(e.target.value)}
          />
          {searchInput && (
            <button
              className="rv-inv-search-clear"
              onClick={() => { setSearchInput(""); setSearch(""); fetchNotes(""); }}
            >
              <CloseIcon />
            </button>
          )}
        </div>
      </div>

      {/* Notes grid */}
      <div className="rv-card rv-inv-card">
        {loading ? (
          <div className="rv-inv-empty">
            <div className="rv-loader" />
            <p>Loading notes…</p>
          </div>
        ) : notes.length === 0 ? (
          <div className="rv-inv-empty">
            <div className="rv-empty-icon"><NoteIcon /></div>
            <p className="rv-empty-title">No notes found</p>
            <p className="rv-empty-sub">
              {search
                ? "Try a different search term."
                : "Create your first customer note to get started."}
            </p>
            {!search && (
              <button
                className="rv-btn rv-btn--primary rv-mt-12"
                onClick={() => setEditModal({ data: {} })}
              >
                <PlusIcon /> New Note
              </button>
            )}
          </div>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 16,
            padding: 16,
          }}>
            {notes.map(note => (
              <NoteCard
                key={note.noteID}
                note={note}
                onView={n   => setViewModal(n)}
                onEdit={n   => setEditModal({ data: n })}
                onDelete={n => setConfirm(n)}
                onHistory={n => setHistory(n)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Icons ── */
function PlusIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function SearchIcon()  { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function EditIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function TrashIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>; }
function CloseIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function CheckIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>; }
function AlertIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>; }
function NoteIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>; }
function HistoryIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/><polyline points="12 7 12 12 15 15"/></svg>; }